import * as React from "react";
import { useEffect, useRef, useState, useMemo } from "react";
import { useLoaderData, useLocation, useNavigate } from "react-router-dom";
import { getMBData, getMBPageData } from "../api/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Loading from "../components/Loading";
import { Helmet } from "react-helmet";
import {
  Box,
  Button,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText
} from "@mui/material";
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from "react-i18next";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { getContent } from "../utils";

export const mbLoader = async () => {
  try {
    const response = await getMBData();

    return response.data;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const mbPageLoader = async (key) => {
  try {
    const response = await getMBPageData(key);

    return response.data;
  } catch (error) {
    console.error(error);
    return null;
  }
};


export const MB = () => {
  const [content, setContent] = useState(null);
  const data = useLoaderData();

  const navigate = useNavigate();
  const location = useLocation();

  const hashId = useMemo(() => {
    const h = location.hash || "";
    return h.startsWith("#") ? h.slice(1) : h;
  }, [location.hash]);

  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const contentRef = useRef(null);
  const getGroupKey = (group) => group.id;

  const mbNavGroups = useMemo(() => content || [], [content]);

  const mbNavItems = mbNavGroups.flatMap((group) => group.items);
  const initialSection = mbNavGroups?.[0]?.items?.[0]?.id;
  const [activeSection, setActiveSection] = useState(initialSection);

  useEffect(() => {
    if (!mbNavItems?.length) return;

    const exists =
      hashId && mbNavItems.some((it) => String(it.id) === String(hashId));

    const next = exists
      ? hashId
      : (mbNavGroups?.[0]?.items?.[0]?.id ?? null);

    if (next && String(next) !== String(activeSection)) {
      setActiveSection(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mbNavItems, hashId]);

  const [pageContent, setPageContent] = useState(null);

  const [expandedGroups, setExpandedGroups] = useState(() => {
    const initialExpanded = {};
    mbNavGroups.forEach((group) => {
      initialExpanded[getGroupKey(group)] = true;
    });
    return initialExpanded;
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    setContent(data);
  }, [data]);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Єдине Україномовне Ком'юніті - ЄУК Mount & Blade",
      "url": "https://uuc.com.ua/mount-and-blade",
      "description": "ЄУК, українські Mount & Blade сервери, український Mount & Blade, Mount & Blade сервери, Mount & Blade ком'юніті, Mount & Blade спільнота, Mount & Blade сервер, українська спільнота"
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (!mbNavGroups) return;
    const initialExpanded = {};
    mbNavGroups.forEach((group) => {
      initialExpanded[getGroupKey(group)] = true;
    });
    setExpandedGroups(initialExpanded);
  }, [mbNavGroups, activeSection]);

  const fetchPageContent = async (key) => {
    const response = await mbPageLoader(key);
    setPageContent(response);
  }

  useEffect(() => {
    if (activeSection) {
      fetchPageContent(activeSection)
    }
  }, [activeSection])

  const handleSectionSelect = (id) => {
    setActiveSection(id);
    setMobileNavOpen(false);

    // Write hash into URL (keeps same pathname, supports back/forward)
    navigate({ hash: `#${id}` }, { replace: false });

    requestAnimationFrame(() => {
      const anchor = document.querySelector("#back-to-top-anchor");
      if (anchor) {
        anchor.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    });
  };

  const activeNavIndex = mbNavItems.findIndex(
    (item) => item.id === activeSection
  );
  const activeNavItem = activeNavIndex >= 0 ? mbNavItems[activeNavIndex] : null;
  const prevItem = activeNavIndex > 0 ? mbNavItems[activeNavIndex - 1] : null;
  const nextItem = activeNavIndex >= 0 && activeNavIndex < mbNavItems.length - 1
    ? mbNavItems[activeNavIndex + 1]
    : null;

  const getNavLabel = (item) => getContent(item, "name", language);

  const MBSection = ({ sectionContent }) => {
    const title = sectionContent ? getContent(sectionContent, "name", language) : "";
    const resolvedTitle = title || (activeNavItem ? getNavLabel(activeNavItem) : "");
    const htmlContent = sectionContent
      ? (getContent(sectionContent, "content", language)
        || getContent(sectionContent, "text", language)
        || "")
      : "";
    const prevLabel = prevItem
      ? `${t("global.nav.previous")}: ${getNavLabel(prevItem)}`
      : t("global.nav.previous");
    const nextLabel = nextItem
      ? `${t("global.nav.next")}: ${getNavLabel(nextItem)}`
      : t("global.nav.next");

    return (
      <Container
        maxWidth="none"
        sx={{
          padding: { xs: "6em 2em 3em 2em !important", md: "4em 4em 4em 4em !important" },
          width: "100%",
          maxWidth: "1200px",
          height: '100%',
          flex: 1,
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          color: "white"
        }}
      >
        {resolvedTitle ? (
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontSize: { xs: 26, md: 40 },
              fontWeight: 700,
              mb: 3
            }}
          >
            {resolvedTitle}
          </Typography>
        ) : null}
        {resolvedTitle ? (
          <Box
            sx={{
              borderTop: "1px solid rgba(255, 255, 255, 0.18)",
              mb: 3
            }}
          />
        ) : null}
        <Box
          sx={{
            flex: 1,
            flexGrow: 1,
            height: 'fit-content',
            fontWeight: 400,
            letterSpacing: 0.4,
            fontSize: { xs: 14, md: 16 },
            lineHeight: 1.7,
            fontFamily: "Montserrat",
            "& p": {
              marginBlock: 0
            },
            "& h1": {
              marginBlock: 1,
            },
            "& h2": {
              marginBlock: 1,
            },
            "& blockquote": {
              margin: "6px 0",
              padding: "2px 0 2px 16px",
              borderLeft: "3px solid rgba(52,197,66,0.65)",
              color: "rgba(255,255,255,0.78)",
            },
            "& blockquote::before": {
              content: "none",
            },
            "& blockquote a": {
              color: "#34C542",
            },
            "& a": {
              color: "#34C542"
            },
            "& img": {
              maxWidth: "100%",
              display: "block",
            },
            "& table": {
              borderCollapse: "collapse",
              margin: "6px 0",
              fontSize: "0.95em",
            },

            "& th, & td": {
              border: "1px solid rgba(255,255,255,0.12)",
              padding: "10px 12px",
              verticalAlign: "top",
            },

            "& th": {
              backgroundColor: "rgba(255,255,255,0.06)",
              fontWeight: 600,
              color: "rgba(255,255,255,0.92)",
            },

            "& td": {
              color: "rgba(255,255,255,0.85)",
            },

            "& tr:nth-of-type(even) td": {
              backgroundColor: "rgba(255,255,255,0.03)",
            },

            "@media (max-width:600px)": {
              "& img": {
                width: "100%",
                height: "auto !important",
              },
              "& table": {
                display: "block",
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
              },
            },
          }}
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
        <Box
          sx={{
            borderTop: "1px solid rgba(255, 255, 255, 0.18)",
            mt: 4,
            mb: 3
          }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            mt: 4,
            flexWrap: "wrap"
          }}
        >
          <Button
            variant="outlined"
            disabled={!prevItem}
            onClick={() => prevItem && handleSectionSelect(prevItem.id)}
            sx={{
              borderColor: "rgba(123, 207, 134, 0.7)",
              color: "rgba(123, 207, 134, 0.9)",
              textTransform: "none",
              fontSize: { xs: 12, md: 13 },
              "&:hover": {
                borderColor: "rgba(104, 187, 115, 0.9)",
                backgroundColor: "rgba(123, 207, 134, 0.08)"
              }
            }}
          >
            {prevLabel}
          </Button>
          <Button
            variant="outlined"
            disabled={!nextItem}
            onClick={() => nextItem && handleSectionSelect(nextItem.id)}
            sx={{
              borderColor: "rgba(123, 207, 134, 0.7)",
              color: "rgba(123, 207, 134, 0.9)",
              textTransform: "none",
              fontSize: { xs: 12, md: 13 },
              "&:hover": {
                borderColor: "rgba(104, 187, 115, 0.9)",
                backgroundColor: "rgba(123, 207, 134, 0.08)"
              }
            }}
          >
            {nextLabel}
          </Button>
        </Box>
      </Container>
    );
  };

  const handleGroupToggle = (label) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  const renderNav = (isMobile = false) => (
    <Box
      sx={{
        width: isMobile ? '100%' : 260,
        p: 2,
        backgroundColor: "#000000",
        paddingTop: { xs: "2em", md: "6.5em", lg: "8.5em" },
        borderRight: "1px solid #242424",
        height: '100%',
        position: 'relative',
        overflowY: "auto",
        overflowX: "hidden",
        boxSizing: "border-box",
        overscrollBehavior: "contain",
      }}
    >
      {mbNavGroups.map((group) => {
        const groupKey = getGroupKey(group);
        return (
          <List key={groupKey} disablePadding sx={{ mb: 1.5 }}>
            <ListItemButton
              onClick={() => handleGroupToggle(groupKey)}
              sx={{
                borderRadius: 2,
                px: 1.5,
                py: 1,
                color: "#A0A0A0",
                "&:hover": {
                  backgroundColor: "#1B1B1B"
                }
              }}
            >
              <ListItemText
                primary={getNavLabel(group)}
                primaryTypographyProps={{
                  fontSize: 11,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  fontWeight: 600
                }}
              />
              {expandedGroups[groupKey] ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </ListItemButton>
            <Collapse in={expandedGroups[groupKey]} timeout="auto" unmountOnExit>
              <List disablePadding sx={{ mt: 0.5 }}>
                {group.items.map((item) => {
                  const isSelected = activeSection === item.id;
                  return (
                    <ListItemButton
                      key={item.id}
                      onClick={() => handleSectionSelect(item.id)}
                      selected={isSelected}
                      sx={{
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.5,
                        ml: 0.5,
                        color: "white",
                        "&.Mui-selected": {
                          backgroundColor: "#34C542",
                          color: "#0B0B0B",
                          "& .MuiListItemText-primary": {
                            fontWeight: 700
                          }
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: "#2FB43D"
                        }
                      }}
                    >
                      <ListItemText
                        primary={getNavLabel(item)}
                        primaryTypographyProps={{
                          fontSize: 14,
                          fontWeight: isSelected ? 700 : 500
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>
          </List>
        );
      })}
    </Box>
  );

  return (
    <>
      {!content ? <Loading /> : null}
      <Helmet>
        <title>Єдине Україномовне Ком'юніті - ЄУК Mount & Blade</title>
        <meta name="description"
              content="«Єдине Україномовне Ком'юніті» – спільнота, що просуває українську мову та україномовний контент в інтернеті" />
        <meta name="keywords"
              content="ЄУК, українські Mount & Blade сервери, український Mount & Blade, Mount & Blade сервери, Mount & Blade ком'юніті, Mount & Blade спільнота, Mount & Blade сервер, українська спільнота" />
        <meta property="og:title" content="Єдине Україномовне Ком'юніті - ЄУК Mount & Blade" />
        <meta property="og:url" content="https://uuc.com.ua/mount-and-blade" />
        <meta property="og:description"
              content="«Єдине Україномовне Ком'юніті» – спільнота, що просуває українську мову та україномовний контент в інтернеті" />
      </Helmet>
      <Header />
      <Container
        maxWidth="none"
        sx={{
          flexGrow: 1,
          flex: 1,
          padding: { xs: "0 0 1em 0 !important", md: "2em 0 0 0 !important" },
          display: 'flex',
          width: "100vw",
          mt: { xs: "73px", md: 0 }
        }}
      >
        <Box
          component="aside"
          sx={{
            position: 'fixed',
            overflow: "hidden",
            top: 0,
            height: "100vh",
            display: { xs: "none", md: "block" },
            alignSelf: "flex-start",
            zIndex: 2,
          }}
        >
          {renderNav()}
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            flex: 1,
            width: "100%",
            overflow: "hidden",
            backgroundColor: "#101010",
          }}
        >
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              justifyContent: "space-between",
              position: 'fixed',
              top: 100,
              left: -5,
            }}
          >
            <IconButton
              onClick={() => setMobileNavOpen(true)}
              sx={{
                color: "white",
                backgroundColor: "#1B1B1B",
                borderRadius: 2,
                "&:hover": {
                  backgroundColor: "#242424"
                }
              }}
            >
              <KeyboardDoubleArrowRightIcon fontSize="large" />
            </IconButton>
          </Box>
          <Box
            ref={contentRef}
            sx={{
              display: 'flex',
              flex: 1,
              padding: { md: "4em 0 0 0 !important" },
              marginLeft: { xs: 0, md: '290px' },
            }}
          >
            <MBSection sectionContent={pageContent} />
          </Box>
        </Box>
      </Container>
      <Drawer
        anchor="left"
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        PaperProps={{
          sx: {
            overflow: 'hidden',
            width: 310,
            borderRight: "1px solid #242424",
          }
        }}
      >
        {renderNav(true)}
      </Drawer>
      <Footer sx={{ marginLeft: { xs: 0, md: '290px' }, width: { xs: '100%', md: "calc(100vw - 290px)" } }} />
    </>
  );
};
